import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useGenerationError } from '../providers/GenerationErrorProvider/GenerationErrorProvider';
import BackendService from '../services/backendService';
import {
  NewPresentationMetadata,
  PromptInputData,
} from '../services/backendService/types';
import {
  GeneratorCoarseLicenseLevel,
  Template,
} from '../services/TemplateService/Template.type';
import { createBlankPresentation, isConnectionError } from '../utils/generationUtils';
import { applySaveAsFreeOverrideIfEnabled } from '../utils/saveGeneratedLessonAsFreeUtils';
import { dailyAbuseLimitKey } from './useDailyAbuseLimit';
import { useFeatureFlag } from './useFeatureFlag';
import { useMetrics } from './useMetrics';
import { presentationsCacheKey } from './usePresentations';
import { useRecentGeneratorsStorage } from './useRecentGenerators';
import useSendSlackMessage from './useSubmitFeedback';
import { templatesCacheKey } from './useTemplates';

const useConsumeGeneratorAsNewPresentation = (tenantId: string, isTestPrep?: boolean) => {
  const metrics = useMetrics();
  const isInternalSaveGeneratedLessonsAsFreeFlagEnabled = useFeatureFlag(
    'internal_save_generated_lessons_as_free',
  );
  const client = useQueryClient();
  const { saveConsumeGenerator } = useRecentGeneratorsStorage();
  const { submit } = useSendSlackMessage();
  const { showGenerationError } = useGenerationError();
  const [livePresentationId, setLivePresentationId] = useState<string | undefined>();
  const {
    mutate: consumeGeneratorRaw,
    isPending: isLoading,
    data,
    isError,
    error,
  } = useMutation({
    mutationFn: async ({
      generatorId,
      variantId,
      generatorName,
      generatorPlan,
      userInputFormData,
      isRetry,
      metadata,
      isPremiumContent,
    }: {
      generatorId: string;
      variantId?: string;
      generatorName: string;
      generatorPlan?: GeneratorCoarseLicenseLevel | null | undefined;
      isRetry?: boolean;
      userInputFormData: PromptInputData[];
      onSuccessFunction?: (template: Template) => void;
      metadata?: NewPresentationMetadata;
      isPremiumContent?: boolean;
    }) => {
      metrics.logEvent('Generator.Consume', {
        generatorId,
        generatorName,
        generatorPlan,
        userInputFormData,
        isRetry,
        isPresentation: true,
      });

      const blank = await createBlankPresentation(metadata);
      if (blank) {
        setLivePresentationId(blank.livePresentationId);
        client.invalidateQueries({
          queryKey: [templatesCacheKey],
        });
        client.invalidateQueries({
          queryKey: [presentationsCacheKey],
        });
      }

      const res = await BackendService.instance.ai.consumeGeneratorAsNewPresentation(
        {
          generatorId,
          generatorName,
          userInputFormData,
          ...(variantId ? { variantId } : {}),
          ...(metadata ? { metadata } : {}),
          // Note: this truthy spread blocks `false` from being sent. If a
          // future change wants to push the save-as-free override into the
          // generation request itself (instead of the post-generation save in
          // applySaveAsFreeOverrideIfEnabled), change this to
          // `isPremiumContent !== undefined` so explicit `false` reaches the
          // backend.
          ...(isPremiumContent ? { isPremiumContent } : {}),
          ...(blank ? { targetPresentationId: blank.id } : {}),
        },
        tenantId,
      );
      // Internal-only: when the "Save generated lessons as free" toggle is on,
      // strip premium markers from the freshly generated lesson's slides so it
      // doesn't consume teaching-session credits for recipients. No-op when
      // the toggle is off.
      return await applySaveAsFreeOverrideIfEnabled(
        res,
        tenantId,
        isInternalSaveGeneratedLessonsAsFreeFlagEnabled,
      );
    },
    onSuccess: (data, { onSuccessFunction, generatorId }) => {
      client.invalidateQueries({
        queryKey: ['hasReachedLimit'],
      });
      client.invalidateQueries({
        queryKey: dailyAbuseLimitKey,
      });
      saveConsumeGenerator(generatorId);
      if (onSuccessFunction && data) {
        onSuccessFunction(data);
      }
      return true;
    },
    onError: (
      error: any,
      { generatorId, generatorName }: { generatorId: string; generatorName?: string },
    ) => {
      submit({
        message: `\n*Error consuming ${
          isTestPrep ? 'test prep lesson' : 'customizable lesson'
        }:* ${
          error.customMessage
        }\n*Generator ID: ${generatorId}\n*Generator Name: ${generatorName}\n*Error message:* ${
          error.message
        }\n*Error status:* ${error.response?.status}\nError code: ${error.code}`,
        accepted: false,
      });
    },
  });

  // Remember the exact args of the most recent call so "Try again" can replay
  // the *whole* mutate invocation — variables AND per-call options — not a
  // reconstruction. (For this transport the redirect lives in variables'
  // onSuccessFunction; useGenerateActivities puts it in the options arg, so
  // replaying both keeps the two transports consistent.)
  const lastCallRef = useRef<Parameters<typeof consumeGeneratorRaw> | null>(null);
  const consumeGenerator = useCallback(
    (...args: Parameters<typeof consumeGeneratorRaw>) => {
      lastCallRef.current = args;
      return consumeGeneratorRaw(...args);
    },
    [consumeGeneratorRaw],
  );

  // Surface failures through the app-wide popup (CURI-362) instead of each form
  // wiring its own. Re-running clears `isError`, so the effect won't re-fire
  // until a fresh failure.
  useEffect(() => {
    if (!isError) return;
    showGenerationError({
      onRetry: () => {
        if (lastCallRef.current) consumeGeneratorRaw(...lastCallRef.current);
      },
      isConnectionError: isConnectionError(error),
    });
  }, [isError, error, showGenerationError, consumeGeneratorRaw]);

  return { consumeGenerator, isLoading, isError, data, error, livePresentationId };
};

export default useConsumeGeneratorAsNewPresentation;
